import { useEffect, useState, useMemo, useCallback } from "react";
import { useAppDispatch } from "../../lib/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../lib/store";
import clsx from "clsx";
import { Button, Input } from "@headlessui/react";
import { toast } from "react-toastify";
import { TailSpin } from "react-loader-spinner";
import { InformationCircleIcon, CheckCircleIcon, QuestionMarkCircleIcon } from "@heroicons/react/16/solid";
import DialogC from "./Dialog";
import { SorobanVaultService, TokenPriceService, IceBoostInfo } from "../../services/soroban-vault.service";
import { useTokenPrice, formatUsd } from "../../hooks/useTokenPrice";
import { StellarService } from "../../services/stellar.service";
import { getAccountInfo, storeAccountBalance } from "../../lib/slices/userSlice";
import aquaLogo from "../../assets/images/aqua_logo.png";
import xlmLogo from "../../assets/images/xlm.png";
import usdcLogo from "../../assets/images/usdc.svg";
import { getErrorMessage } from "../../utils/errors";
import { useAutoCompound } from "../../hooks/useAutoCompound";
import { apiService, VaultIncomeResponse } from "../../services/api.service";

// Locale-safe number formatter — always uses dot as decimal separator
const fmtNum = (val: string | number, decimals = 4): string =>
  parseFloat(String(val)).toFixed(decimals).replace(/,/g, ".");

// Tokens that may NOT be used for a single-asset vault deposit. A single-sided
// BLUB deposit pushes BLUB-AQUA further off ratio, which is the opposite of what
// the pool needs; AQUA-only entry is the supported single-asset path.
const SINGLE_ASSET_BLOCKED_CODES = ["BLUB"];

const VAULT_APY_INFO =
  "Pool APY on Aquarius plus WhaleHub rewards.\n\nWhaleHub pays 30% of the protocol's AQUA income into this vault as extra liquidity, without minting new vault shares, so every backer's slice grows.\n\nMeasured from the rewards actually paid in the last 7 days, annualised against the vault's current value. It moves as rewards and deposits change: more deposits share the same rewards. Not a promise of future returns.";

// The POOL's own get_tokens() ordering for pool 0, which is sorted by contract
// address: index 0 = AQUA, index 1 = BLUB. This is NOT PoolInfo's
// (token_a = BLUB, token_b = AQUA) — the two orders are reversed, and
// `vault_withdraw_single` takes the pool's.
const POOL_COIN_ORDER = ["AQUA", "BLUB"] as const;


// Vault bucket that holds the single-sided-AQUA reward class. It points at the
// SAME Aquarius pool as pool 0 but is a separate `PoolInfo`, so Stream B can pay
// it 70% while the balanced class gets 30%. A single-asset deposit MUST land
// here or the depositor silently joins the balanced class instead.
//
// Unset until `add_pool` has been called on-chain; while unset, single-asset
// deposits fall back to the selected pool (current behaviour, no 70% class).
const SINGLE_AQUA_POOL_ID = (() => {
  const raw = process.env.REACT_APP_SINGLE_AQUA_POOL_ID;
  const n = Number(raw);
  return raw && Number.isInteger(n) && n > 0 ? n : null;
})();

const TOKEN_LOGOS: Record<string, string> = {
  AQUA: aquaLogo,
  XLM: xlmLogo,
  USDC: usdcLogo,
  BLUB: "/Blub_logo2.svg",
};

interface PoolInfo {
  pool_id: number;
  pool_address: string;
  token_a: string;
  token_b: string;
  share_token: string;
  total_lp_tokens: string;
  active: boolean;
  added_at: number;
  // Display info
  token_a_code: string;
  token_b_code: string;
  token_a_logo: string;
  token_b_logo: string;
}

interface UserPosition {
  pool_id: number;
  share_ratio: string;
  deposited_at: number;
  active: boolean;
  // Calculated fields
  user_lp_amount: string;
  percentage: string;
}

function AddLiquidity() {
  const aquaPrice = useTokenPrice("AQUA");
  const blubPrice = useTokenPrice("BLUB");
  const [pools, setPools] = useState<PoolInfo[]>([]);
  const [selectedPool, setSelectedPool] = useState<PoolInfo | null>(null);
  const [userPositions, setUserPositions] = useState<UserPosition[]>([]);

  const [depositAmount1, setDepositAmount1] = useState<string>("");
  const [depositAmount2, setDepositAmount2] = useState<string>("");
  const [withdrawPercent, setWithdrawPercent] = useState<number>(100);
  // Withdrawal mode. `vault_withdraw` returns both legs pro-rata; someone who
  // entered with AQUA only would otherwise leave holding BLUB they have to sell.
  // null = both tokens (default), otherwise the POOL's get_tokens() index —
  // pool 0 is [AQUA, BLUB], which is NOT PoolInfo's (token_a=BLUB, token_b=AQUA).
  const [withdrawCoinIndex, setWithdrawCoinIndex] = useState<number | null>(null);
  // LP quote for the pending deposit, priced by the POOL itself.
  // undefined = not yet quoted, null = quote unavailable (show so, never guess).
  const [quotedLp, setQuotedLp] = useState<string | null | undefined>(undefined);



  const [isDepositing, setIsDepositing] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [isLoadingPools, setIsLoadingPools] = useState(true);

  // Token balances
  const [balanceA, setBalanceA] = useState<string>("0");
  const [balanceB, setBalanceB] = useState<string>("0");
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);

  // Pool reserves for withdrawal estimates
  const [reserveA, setReserveA] = useState<string>("0");
  const [reserveB, setReserveB] = useState<string>("0");
  const [totalLpSupply, setTotalLpSupply] = useState<string>("0");

  // Compound stats
  const [compoundStats, setCompoundStats] = useState<{
    totalCompoundedLp: string; totalRewardsClaimed: string; compoundCount: number; lastCompoundTime: number;
  } | null>(null);
  const [userCompoundGains, setUserCompoundGains] = useState<{
    currentLp: string; depositedLp: string; compoundGainLp: string;
  } | null>(null);

  const [activeTab, setActiveTab] = useState<"deposit" | "withdraw">("deposit");

  // Position USD value derived from pool reserves and token prices
  const positionValueUsd = useMemo(() => {
    const pos = userPositions[0];
    if (!pos || !selectedPool) return null;
    const userLp = parseFloat(pos.user_lp_amount || "0");
    const totalLp = parseFloat(totalLpSupply);
    if (totalLp <= 0 || userLp <= 0) return null;
    const userShare = userLp / totalLp;
    const resA = parseFloat(reserveA);
    const resB = parseFloat(reserveB);
    const priceA = selectedPool.token_a_code === "BLUB" ? blubPrice : selectedPool.token_a_code === "AQUA" ? aquaPrice : 0;
    const priceB = selectedPool.token_b_code === "BLUB" ? blubPrice : selectedPool.token_b_code === "AQUA" ? aquaPrice : 0;
    return userShare * (resA * priceA + resB * priceB);
  }, [userPositions, totalLpSupply, reserveA, reserveB, selectedPool, aquaPrice, blubPrice]);

  // WhaleHub reward boost: Stream B AQUA that the backend paid into this vault
  // bucket over the last 7 days (admin_compound_deposit, no shares minted), annualised
  // against the bucket's value. Simple rate: the income does not grow with the vault.
  const [vaultIncome, setVaultIncome] = useState<VaultIncomeResponse | null>(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => apiService.getVaultIncome().then((r) => { if (!cancelled) setVaultIncome(r); });
    load();
    const t = setInterval(load, 10 * 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const boostApr = useMemo(() => {
    if (!selectedPool || !vaultIncome?.complete) return null;
    const inc = vaultIncome.pools[String(selectedPool.pool_id)];
    if (!inc) return 0;
    // Valued in AQUA units, BLUB = 1 AQUA (the rate TokenPriceService uses), so
    // no USD price is needed: phones often get no CoinGecko answer.
    const totalLp = parseFloat(totalLpSupply);
    const vaultLp = parseFloat(selectedPool.total_lp_tokens || "0") / 1e7;
    const poolValue = parseFloat(reserveA) + parseFloat(reserveB);
    if (!(totalLp > 0) || !(vaultLp > 0) || !(poolValue > 0) || !(vaultIncome.windowDays > 0)) return null;
    const vaultValue = (vaultLp / totalLp) * poolValue;
    const income = (Number(inc.amountA) + Number(inc.amountB)) / 1e7;
    return (income * (365 / vaultIncome.windowDays) / vaultValue) * 100;
  }, [selectedPool, vaultIncome, totalLpSupply, reserveA, reserveB]);

  const formatPositionUsd = (n: number) =>
    n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Pool APY from Aquarius
  const [poolApy, setPoolApy] = useState<string>("--");
  // Pool APY (Aquarius, ~0.2%) + WhaleHub boost. If Aquarius doesn't answer, show the boost alone.
  const vaultApy = boostApr === null ? null : (parseFloat(poolApy) || 0) + boostApr;
  const fmtApy = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: n >= 100 ? 0 : 2 });

  const [iceBoost, setIceBoost] = useState<IceBoostInfo | null>(null);

  // AQUA-only is the default entry: it is the class WhaleHub incentivizes.
  const [singleAsset, setSingleAsset] = useState<boolean>(true);
  const [singleAssetToken, setSingleAssetToken] = useState<"a" | "b">("a");

  // Single-asset entry options for the selected pool, minus blocked tokens.
  const singleAssetOptions = useMemo(() => {
    if (!selectedPool) return [] as { key: "a" | "b"; code: string }[];
    return (
      [
        { key: "a" as const, code: selectedPool.token_a_code },
        { key: "b" as const, code: selectedPool.token_b_code },
      ] as { key: "a" | "b"; code: string }[]
    ).filter((t) => !SINGLE_ASSET_BLOCKED_CODES.includes(t.code));
  }, [selectedPool]);

  // Keep the selection on an allowed token. Pool token order comes from the
  // contract (pool 0 is token_a = BLUB, token_b = AQUA), so never assume a key.
  useEffect(() => {
    if (singleAssetOptions.length === 0) return;
    if (!singleAssetOptions.some((t) => t.key === singleAssetToken)) {
      setSingleAssetToken(singleAssetOptions[0].key);
    }
  }, [singleAssetOptions, singleAssetToken]);

  // Slippage tolerance in percentage (e.g., 0.5 = 0.5%)
  const [slippageTolerance, setSlippageTolerance] = useState<number>(0.5);
  const [showSlippageSettings, setShowSlippageSettings] = useState<boolean>(false);
  const [customSlippage, setCustomSlippage] = useState<string>("");

  const [dialogMsg, setDialogMsg] = useState<string>("");
  const [dialogTitle, setDialogTitle] = useState<string>("");
  const [openDialog, setOptDialog] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.user);
  const userWalletAddress = user?.userWalletAddress;
  const ac = useAutoCompound(userWalletAddress, user?.walletName as any);

  // Create vaultService only once
  const vaultService = useMemo(() => new SorobanVaultService(), []);

  const loadPools = useCallback(async () => {
    try {
      setIsLoadingPools(true);
      console.log("[AddLiquidity] Loading pools from contract...");

      const poolCount = await vaultService.getPoolCount();
      console.log("[AddLiquidity] Pool count:", poolCount);

      if (poolCount === 0) {
        console.log("[AddLiquidity] No pools configured in contract");
        setPools([]);
        setSelectedPool(null);
        return;
      }

      const loadedPools: PoolInfo[] = [];
      // Only load pool 0 (BLUB-AQUA) — other pools are hidden
      try {
        const poolInfo = await vaultService.getPoolInfo(0);
        console.log(`[AddLiquidity] Pool 0:`, poolInfo);
        if (poolInfo.active) {
          loadedPools.push(poolInfo);
        }
      } catch (poolError) {
        console.error(`[AddLiquidity] Failed to load pool 0:`, poolError);
      }

      setPools(loadedPools);
      if (loadedPools.length > 0) {
        setSelectedPool((prev) => prev ?? loadedPools[0]);
      }
    } catch (error) {
      console.error("[AddLiquidity] Failed to load pools:", error);
      toast.error("Failed to load pools. Please check your connection.");
    } finally {
      setIsLoadingPools(false);
    }
  }, [vaultService]);

  const loadUserPosition = useCallback(async (poolId: number, totalLpTokens?: string) => {
    if (!userWalletAddress) return;

    try {
      const [position, poolStats, gains] = await Promise.all([
        vaultService.getUserVaultPosition(userWalletAddress, poolId, totalLpTokens),
        vaultService.getPoolCompoundStats(poolId),
        vaultService.getUserCompoundGains(userWalletAddress, poolId),
      ]);

      if (position) {
        setUserPositions([position]);
      } else {
        setUserPositions([]);
      }
      setCompoundStats(poolStats);
      setUserCompoundGains(gains);
    } catch (error) {
      console.error("Failed to load user position:", error);
      setUserPositions([]);
    }
  }, [vaultService, userWalletAddress]);

  const loadBalances = useCallback(async (pool: PoolInfo) => {
    if (!userWalletAddress) {
      setBalanceA("0");
      setBalanceB("0");
      return;
    }

    setIsLoadingBalances(true);
    try {
      const [balA, balB] = await Promise.all([
        vaultService.getTokenBalance(pool.token_a, userWalletAddress),
        vaultService.getTokenBalance(pool.token_b, userWalletAddress),
      ]);
      setBalanceA(balA);
      setBalanceB(balB);
    } catch (error) {
      console.error("Failed to load balances:", error);
      setBalanceA("0");
      setBalanceB("0");
    } finally {
      setIsLoadingBalances(false);
    }
  }, [vaultService, userWalletAddress]);

  const loadPoolReserves = useCallback(async (pool: PoolInfo) => {
    try {
      const data = await vaultService.getPoolReserves(
        pool.pool_address,
        pool.share_token,
        pool.token_a,
        pool.token_b,
      );
      setReserveA(data.reserveA);
      setReserveB(data.reserveB);
      setTotalLpSupply(data.totalLpSupply);
    } catch (error) {
      console.error("Failed to load pool reserves:", error);
      setReserveA("0");
      setReserveB("0");
      setTotalLpSupply("0");
    }
  }, [vaultService]);

  const loadPoolApy = useCallback(async (pool: PoolInfo) => {
    const { poolApy: apy, totalShare } = await vaultService.getAquariusPoolApy(pool.pool_address);
    setPoolApy(apy);
    // Override on-chain totalLpSupply with Aquarius API value — avoids inflated on-chain values
    // that would cause min_shares to be too high (error #2006)
    if (totalShare && parseFloat(totalShare) > 0) {
      setTotalLpSupply(totalShare);
    }
    // Fetch ICE boost info (non-blocking — don't delay APY display)
    vaultService.getIceBoostInfo(pool.pool_address, pool.share_token, totalShare)
      .then(setIceBoost)
      .catch(() => setIceBoost(null));
  }, [vaultService]);

  // Refresh wallet balances and update Redux state (like other sections do)
  const refreshWalletAndBalances = useCallback(async () => {
    if (!userWalletAddress || !selectedPool) return;

    try {
      // Update Redux state with fresh Horizon data
      const stellarService = new StellarService();
      const wrappedAccount = await stellarService.loadAccount(userWalletAddress);
      dispatch(getAccountInfo(userWalletAddress));
      dispatch(storeAccountBalance(wrappedAccount.balances));

      // Also refresh local token balances
      await loadBalances(selectedPool);
      await loadUserPosition(selectedPool.pool_id, selectedPool.total_lp_tokens);
      await loadPoolReserves(selectedPool);
    } catch (error) {
      console.error("Failed to refresh wallet balances:", error);
    }
  }, [userWalletAddress, selectedPool, dispatch, loadBalances, loadUserPosition, loadPoolReserves]);

  // Load pools on mount
  useEffect(() => {
    loadPools();
  }, [loadPools]);

  // Retry pool loading when wallet connects and pools are empty (handles reconnect case)
  useEffect(() => {
    if (userWalletAddress && pools.length === 0 && !isLoadingPools) {
      loadPools();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userWalletAddress]);

  // Load pool reserves and APY when pool is selected
  useEffect(() => {
    if (selectedPool) {
      loadPoolReserves(selectedPool);
      loadPoolApy(selectedPool);
    }
  }, [selectedPool?.pool_id, loadPoolReserves, loadPoolApy]);

  // Load user position and balances when pool or wallet changes
  useEffect(() => {
    if (selectedPool && userWalletAddress) {
      loadUserPosition(selectedPool.pool_id, selectedPool.total_lp_tokens);
      loadBalances(selectedPool);
    } else {
      // Reset balances if no wallet connected
      setBalanceA("0");
      setBalanceB("0");
      setUserPositions([]);
    }
  }, [selectedPool?.pool_id, userWalletAddress, loadUserPosition, loadBalances]);

  // Auto-refresh balances every 30 seconds (like Yield.tsx does)
  useEffect(() => {
    if (userWalletAddress && selectedPool) {
      // Set up auto-refresh every 30 seconds for real-time updates
      const refreshInterval = setInterval(() => {
        refreshWalletAndBalances();
      }, 30000);

      // Cleanup interval on unmount
      return () => clearInterval(refreshInterval);
    }
  }, [userWalletAddress, selectedPool, refreshWalletAndBalances]);

  // Normalize user input: replace commas with dots so European locale keyboards work correctly
  const normalizeDecimal = (value: string) => value.replace(/,/g, ".");

  // Auto-fill second field from pool ratio
  const handleAmount1Change = (value: string) => {
    const normalized = normalizeDecimal(value);
    setDepositAmount1(normalized);
    if (singleAsset) return;
    const amt = parseFloat(normalized);
    const resA = parseFloat(reserveA);
    const resB = parseFloat(reserveB);
    if (!isNaN(amt) && amt > 0 && resA > 0 && resB > 0) {
      setDepositAmount2((amt * (resB / resA)).toFixed(7));
    } else if (!normalized) {
      setDepositAmount2("");
    }
  };

  const handleAmount2Change = (value: string) => {
    const normalized = normalizeDecimal(value);
    setDepositAmount2(normalized);
    if (singleAsset) return;
    const amt = parseFloat(normalized);
    const resA = parseFloat(reserveA);
    const resB = parseFloat(reserveB);
    if (!isNaN(amt) && amt > 0 && resA > 0 && resB > 0) {
      setDepositAmount1((amt * (resA / resB)).toFixed(7));
    } else if (!normalized) {
      setDepositAmount1("");
    }
  };

  // LP for a deposit is quoted by the pool via calc_token_amount, not computed
  // here. The previous local estimate used constant-product math
  // ((amount / reserve) * totalLp, min of both legs), which is wrong twice on a
  // StableSwap pool: it overstated a 1,000 AQUA deposit by ~43x against live
  // reserves, and Math.min(x, 0) returned ZERO for every single-sided deposit.
  useEffect(() => {
    if (!selectedPool) return;
    const a = parseFloat(depositAmount1 || "0");
    const b = parseFloat(depositAmount2 || "0");
    if (!(a > 0) && !(b > 0)) {
      setQuotedLp(undefined);
      return;
    }

    let cancelled = false;
    setQuotedLp(undefined);
    // Debounced: this is an RPC round trip on every keystroke otherwise.
    const t = setTimeout(async () => {
      // calc_token_amount takes the POOL's get_tokens() order — [AQUA, BLUB] —
      // while depositAmount1/2 follow PoolInfo's (token_a = BLUB, token_b = AQUA).
      const aqua = singleAsset
        ? (singleAssetToken === "b" ? String(a) : "0")
        : String(b);
      const blub = singleAsset
        ? (singleAssetToken === "a" ? String(a) : "0")
        : String(a);
      const lp = await vaultService.quoteDepositLp(selectedPool.pool_address, aqua, blub);
      if (!cancelled) setQuotedLp(lp);
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [selectedPool, depositAmount1, depositAmount2, singleAsset, singleAssetToken]);

  // Apply slippage tolerance to get minimum acceptable amount (human-readable)
  const applySlippage = (amount: string | number): string => {
    const value = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(value) || value <= 0) return "0";

    const minAmount = value * (1 - slippageTolerance / 100);
    return minAmount.toString();
  };

  // Handle slippage preset selection
  const handleSlippagePreset = (value: number) => {
    setSlippageTolerance(value);
    setCustomSlippage("");
  };

  // Handle custom slippage input
  const handleCustomSlippageChange = (value: string) => {
    setCustomSlippage(value);
    const parsed = parseFloat(value);
    if (!isNaN(parsed) && parsed > 0 && parsed <= 50) {
      setSlippageTolerance(parsed);
    }
  };

  // Calculate estimated withdrawal amounts
  const getEstimatedWithdrawAmounts = () => {
    if (!userPosition || !selectedPool) {
      return { estimatedA: "0.00", estimatedB: "0.00" };
    }

    const userLp = parseFloat(userPosition.user_lp_amount || "0");
    const poolTotalLp = parseFloat(totalLpSupply);
    const resA = parseFloat(reserveA);
    const resB = parseFloat(reserveB);

    if (poolTotalLp <= 0 || userLp <= 0) {
      return { estimatedA: "0.00", estimatedB: "0.00" };
    }

    // User's share of the entire Aquarius pool
    const userShareOfPool = userLp / poolTotalLp;
    // Apply withdrawal percentage
    const withdrawShare = (withdrawPercent / 100) * userShareOfPool;

    const estimatedA = (withdrawShare * resA).toFixed(4);
    const estimatedB = (withdrawShare * resB).toFixed(4);

    return { estimatedA, estimatedB };
  };

  const handleDeposit = async () => {
    if (!selectedPool) {
      return toast.warn("Please select a pool");
    }

    if (!user?.userWalletAddress) {
      return toast.warn("Please connect your wallet");
    }

    if (!user?.walletName) {
      return toast.warn("Please connect your wallet");
    }

    const amount1 = parseFloat(depositAmount1);
    let amount2 = parseFloat(depositAmount2);

    // In single-asset mode, validate against the selected token's balance
    if (singleAsset) {
      const selectedCode = singleAssetToken === "a" ? selectedPool.token_a_code : selectedPool.token_b_code;
      const selectedBal = parseFloat(singleAssetToken === "a" ? balanceA : balanceB);

      if (!amount1 || amount1 <= 0) {
        return toast.warn(`Please enter ${selectedCode} amount`);
      }
      if (amount1 > selectedBal) {
        return toast.warn(
          `Insufficient ${selectedCode} balance. You have ${selectedBal.toFixed(4)}`
        );
      }
      amount2 = 0;
    } else {
      if (!amount1 || amount1 <= 0) {
        return toast.warn(`Please enter ${selectedPool.token_a_code} amount`);
      }

      // If the second field is empty/zero, auto-calculate from pool ratio
      if (!amount2 || amount2 <= 0) {
        const resA = parseFloat(reserveA);
        const resB = parseFloat(reserveB);
        if (resA > 0 && resB > 0) {
          amount2 = amount1 * (resB / resA);
          setDepositAmount2(amount2.toFixed(7));
        } else {
          return toast.warn(`Please enter ${selectedPool.token_b_code} amount`);
        }
      }

      // Balance validation
      const balA = parseFloat(balanceA);
      const balB = parseFloat(balanceB);

      if (amount1 > balA) {
        return toast.warn(
          `Insufficient ${selectedPool.token_a_code} balance. You have ${balA.toFixed(4)}`
        );
      }

      if (amount2 > 0 && amount2 > balB) {
        return toast.warn(
          `Insufficient ${selectedPool.token_b_code} balance. You have ${balB.toFixed(4)}`
        );
      }
    }

    // Minimum $1 deposit validation
    setIsDepositing(true);

    try {
      const usdTokenACode = singleAsset && singleAssetToken === "b" ? selectedPool.token_b_code : selectedPool.token_a_code;
      const usdAmountA = singleAsset && singleAssetToken === "b" ? 0 : amount1;
      const usdTokenBCode = singleAsset && singleAssetToken === "b" ? selectedPool.token_a_code : selectedPool.token_b_code;
      const usdAmountB = singleAsset ? (singleAssetToken === "b" ? amount1 : 0) : amount2;
      const totalUsdValue = await TokenPriceService.calculateTotalUsdValue(
        usdTokenACode,
        usdAmountA,
        usdTokenBCode,
        usdAmountB,
        parseFloat(reserveA),
        parseFloat(reserveB)
      );

      // Only enforce minimum if we successfully got a price (0 means price unavailable)
      if (totalUsdValue > 0 && totalUsdValue < 1) {
        setIsDepositing(false);
        return toast.error(
          `Minimum deposit amount is $1. Your deposit is worth $${totalUsdValue.toFixed(2)}`
        );
      }
    } catch (priceError) {
      console.warn("Could not verify USD value, proceeding with deposit:", priceError);
      // Continue with deposit if price check fails (graceful degradation)
    }

    try {
      let result;

      if (singleAsset) {
        const tokenIn = singleAssetToken === "a" ? selectedPool.token_a : selectedPool.token_b;
        // Route into the single-sided-AQUA reward class when it exists; only
        // pool 0 has a paired class, so other pools keep their own id.
        const singleAssetPoolId =
          SINGLE_AQUA_POOL_ID != null && selectedPool.pool_id === 0
            ? SINGLE_AQUA_POOL_ID
            : selectedPool.pool_id;
        result = await vaultService.vaultDepositSingle({
          userAddress: user.userWalletAddress,
          poolId: singleAssetPoolId,
          tokenIn,
          amountIn: amount1.toString(),
          minShares: "0",
          walletName: user.walletName,
        });
      } else {
        result = await vaultService.vaultDeposit({
          userAddress: user.userWalletAddress,
          poolId: selectedPool.pool_id,
          desiredA: amount1.toString(),
          desiredB: amount2.toString(),
          minShares: "0",
          walletName: user.walletName,
        });
      }

      if (result.success) {
        toast.success("Deposit successful!");
        setDepositAmount1("");
        setDepositAmount2("");
        // Refresh all balances including Redux state
        await refreshWalletAndBalances();
      } else {
        toast.error(result.error || "Deposit failed");
      }
    } catch (error: any) {
      console.error("Deposit error:", error);
      toast.error(getErrorMessage(error, "Deposit failed"));
    } finally {
      setIsDepositing(false);
    }
  };

  const handleWithdraw = async () => {
    if (!selectedPool) {
      return toast.warn("Please select a pool");
    }

    if (!user?.userWalletAddress) {
      return toast.warn("Please connect your wallet");
    }

    if (userPositions.length === 0) {
      return toast.warn("No position to withdraw");
    }

    if (withdrawPercent <= 0 || withdrawPercent > 100) {
      return toast.warn("Invalid withdrawal percentage");
    }

    if (!user?.walletName) {
      return toast.warn("Please connect your wallet");
    }

    setIsWithdrawing(true);

    try {
      // Calculate minimum amounts with slippage protection
      const { estimatedA, estimatedB } = getEstimatedWithdrawAmounts();
      const minAWithSlippage = applySlippage(estimatedA);
      const minBWithSlippage = applySlippage(estimatedB);

      console.log("[Withdraw] Slippage protection:", {
        estimatedA,
        estimatedB,
        slippageTolerance: `${slippageTolerance}%`,
        minAWithSlippage,
        minBWithSlippage,
      });

      const result =
        withdrawCoinIndex == null
          ? await vaultService.vaultWithdraw({
              userAddress: user.userWalletAddress,
              poolId: selectedPool.pool_id,
              sharePercent: withdrawPercent * 100, // Convert to basis points
              minA: minAWithSlippage,
              minB: minBWithSlippage,
              walletName: user.walletName,
            })
          : await vaultService.vaultWithdrawSingle({
              userAddress: user.userWalletAddress,
              poolId: selectedPool.pool_id,
              sharePercent: withdrawPercent * 100,
              coinIndex: withdrawCoinIndex,
              // Pool index 0 is AQUA, 1 is BLUB. PoolInfo has token_a = BLUB and
              // token_b = AQUA, so estimatedA/B map the other way round.
              minAmount:
                withdrawCoinIndex === 0 ? minBWithSlippage : minAWithSlippage,
              walletName: user.walletName,
            });

      if (result.success) {
        toast.success("Withdrawal successful!");
        setWithdrawPercent(100);
        // Refresh all balances including Redux state
        await refreshWalletAndBalances();
      } else {
        toast.error(result.error || "Withdrawal failed");
      }
    } catch (error: any) {
      console.error("Withdrawal error:", error);
      toast.error(getErrorMessage(error, "Withdrawal failed"));
    } finally {
      setIsWithdrawing(false);
    }
  };

  const onDialogOpen = (msg: string, title: string) => {
    setOptDialog(true);
    setDialogMsg(msg);
    setDialogTitle(title);
  };

  const closeModal = () => {
    setOptDialog(false);
  };

  const userPosition = userPositions[0];

  if (isLoadingPools) {
    return (
      <div className="bg-[#0E111BCC] p-10 rounded-[16px] flex justify-center items-center min-h-[400px]">
        <TailSpin
          height="40"
          width="40"
          color="#00CC99"
          ariaLabel="tail-spin-loading"
          radius="1"
          visible={true}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="bg-[#0E111BCC] p-6 md:p-8 rounded-[16px]">
        {/* Header */}
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base md:text-xl font-semibold text-white">AQUA-BLUB Liquidity Vault</h2>
          <div className="flex items-center gap-2 shrink-0">
            <InformationCircleIcon
              className="h-4 w-4 text-[#6B7280] cursor-pointer hover:text-white transition-colors"
              onClick={() =>
                onDialogOpen(
                  "You're joining a crowdfunded liquidity pool. Every backer contributes both AQUA and BLUB, and everyone earns a proportional share of what the pool generates.\n\nWhen traders swap AQUA and BLUB, the pool keeps a small fee. Your LP tokens are your backer share. The more you contribute, the bigger your slice of those fees.\n\nOn top of that, the pool earns rewards from Aquarius. Both fees and rewards are automatically reinvested into your position every 4 hours. No buttons to press, no manual claiming.\n\nWithdraw your share anytime. No lockups.",
                  "AQUA-BLUB Liquidity Vault"
                )
              }
            />
            <button
              onClick={async () => {
                await loadPools();
                if (selectedPool && userWalletAddress) await refreshWalletAndBalances();
              }}
              disabled={isLoadingPools}
              title="Refresh"
              className="text-[#6B7280] hover:text-white transition-colors disabled:opacity-40"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${isLoadingPools ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              className="flex items-center space-x-1.5 text-xs text-[#6B7280] hover:text-white transition-colors border border-[#2A3050] rounded-[6px] px-2.5 py-1.5"
              onClick={() => setShowSlippageSettings(!showSlippageSettings)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Slippage: {slippageTolerance}%</span>
            </button>
          </div>
        </div>

        {/* Slippage panel */}
        {showSlippageSettings && (
          <div className="mt-3 p-4 bg-[#0A0D14] border border-[#1C2235] rounded-[10px]">
            <div className="flex items-center space-x-2 mb-3">
              <span className="text-xs text-[#B1B3B8]">Max price impact you're willing to accept</span>
              <InformationCircleIcon
                className="h-3.5 w-3.5 text-[#6B7280] cursor-pointer"
                onClick={() => onDialogOpen(
                  "The maximum price difference you'll accept between when you click deposit and when the transaction completes.\n\nLower setting = more protection but higher chance the transaction fails. Higher setting = transaction succeeds more often but you might pay slightly more than expected.\n\nDefault 0.5% is fine for most deposits.",
                  "Slippage Tolerance"
                )}
              />
            </div>
            <div className="flex items-center space-x-2">
              {[0.5, 1, 2, 3].map((preset) => (
                <button
                  key={preset}
                  className={clsx(
                    "px-3 py-1.5 rounded-[6px] text-xs font-medium transition-colors",
                    slippageTolerance === preset && !customSlippage
                      ? "bg-[#00CC99] text-white"
                      : "bg-[#1C2235] text-[#B1B3B8] hover:bg-[#2A3050]"
                  )}
                  onClick={() => handleSlippagePreset(preset)}
                >
                  {preset}%
                </button>
              ))}
              <div className="flex items-center bg-[#1C2235] rounded-[6px] px-2 border border-[#2A3050]">
                <input
                  type="number"
                  placeholder="Custom"
                  value={customSlippage}
                  onChange={(e) => handleCustomSlippageChange(e.target.value)}
                  className="w-14 bg-transparent text-white text-xs py-1.5 focus:outline-none"
                  min="0.1" max="50" step="0.1"
                />
                <span className="text-[#6B7280] text-xs">%</span>
              </div>
            </div>
            {slippageTolerance > 5 && (
              <div className="mt-2 text-[#FF9900] text-xs">High slippage may result in an unfavorable rate</div>
            )}
            {slippageTolerance < 0.1 && (
              <div className="mt-2 text-[#FF9900] text-xs">Low slippage may cause transaction to fail</div>
            )}
          </div>
        )}

        {/* Pool Selection — only BLUB-AQUA, no dropdown needed */}
        <div className="mt-5">
          {pools.length === 0 ? (
            <div className="w-full bg-[#0A0D14] text-[#6B7280] p-6 rounded-[10px] text-center border border-[#1C2235]">
              <span className="text-sm">No liquidity pools available</span>
            </div>
          ) : (
            <div className="w-full bg-[#0A0D14] text-white p-3 rounded-[10px] border border-[#1C2235] text-sm flex items-center gap-3">
              <div className="flex items-center -space-x-1">
                <img src={TOKEN_LOGOS["BLUB"]} alt="BLUB" className="w-5 h-5 rounded-full flex-shrink-0" />
                <img src={TOKEN_LOGOS["AQUA"]} alt="AQUA" className="w-5 h-5 rounded-full flex-shrink-0" />
              </div>
              <span>BLUB / AQUA</span>
            </div>
          )}
        </div>

        {/* APY Cards + Reserves */}
        {selectedPool && (
          <div className="mt-5 space-y-3">
            {/* Banner */}
            <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-3 mb-4 text-sm text-gray-200">
              You're a backer in a crowdfunded liquidity pool. You earn swap fees plus AQUA rewards, reinvested for you every 4 hours.
            </div>

            {/* ICE Boost Info — hidden until boost is routed through admin
            {iceBoost && iceBoost.ourLp > 0 && (
              <div className="bg-[#0A0D14] border border-[#8B5CF6]/20 rounded-[10px] p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-[#6B7280] mb-1">ICE Boost</div>
                    <div className={`text-xl font-bold ${iceBoost.boost >= 2.49 ? "text-[#8B5CF6]" : iceBoost.boost > 1.01 ? "text-[#A78BFA]" : "text-[#6B7280]"}`}>
                      {iceBoost.boost.toFixed(2)}x
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] text-[#6B7280]">
                      Pool share: {iceBoost.lpSharePct < 0.01 ? "<0.01" : iceBoost.lpSharePct.toFixed(2)}%
                    </div>
                    <div className="text-[10px] text-[#6B7280]">
                      ICE: {(iceBoost.myIce / 1e6).toFixed(1)}M / {(iceBoost.totalIce / 1e9).toFixed(0)}B
                    </div>
                    {iceBoost.boost >= 2.49 ? (
                      <div className="text-[10px] text-[#8B5CF6] mt-0.5">Max boost active</div>
                    ) : (
                      <div className="text-[10px] text-[#6B7280] mt-0.5">
                        2.5x up to {iceBoost.maxLpFor2_5x.toFixed(0)} LP
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            */}

            {/* Pool Reserves */}
            <div className="bg-[#0A0D14] border border-[#1C2235] rounded-[10px] p-4">
              <div className="text-xs text-[#6B7280] uppercase tracking-wider mb-2">Pool Reserves</div>
              <div className="flex justify-between text-sm">
                <div className="flex items-center space-x-1.5">
                  {TOKEN_LOGOS[selectedPool.token_a_code] && (
                    <img src={TOKEN_LOGOS[selectedPool.token_a_code]} alt={selectedPool.token_a_code} className="w-4 h-4 rounded-full flex-shrink-0" />
                  )}
                  <span className="text-[#B1B3B8]">{selectedPool.token_a_code}</span>
                </div>
                <span className="text-white font-medium">
                  {parseFloat(reserveA) > 0
                    ? <>
                        {parseFloat(reserveA).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {(() => {
                          const p = selectedPool.token_a_code === "BLUB" ? blubPrice : selectedPool.token_a_code === "AQUA" ? aquaPrice : 0;
                          return parseFloat(reserveA) > 0 ? <span className="text-[#6B7280] text-xs ml-1">{formatUsd(reserveA, p)}</span> : null;
                        })()}
                      </>
                    : <span className="text-[#6B7280]">Loading...</span>}
                </span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <div className="flex items-center space-x-1.5">
                  {TOKEN_LOGOS[selectedPool.token_b_code] && (
                    <img src={TOKEN_LOGOS[selectedPool.token_b_code]} alt={selectedPool.token_b_code} className="w-4 h-4 rounded-full flex-shrink-0" />
                  )}
                  <span className="text-[#B1B3B8]">{selectedPool.token_b_code}</span>
                </div>
                <span className="text-white font-medium">
                  {parseFloat(reserveB) > 0
                    ? <>
                        {parseFloat(reserveB).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {(() => {
                          const p = selectedPool.token_b_code === "BLUB" ? blubPrice : selectedPool.token_b_code === "AQUA" ? aquaPrice : 0;
                          return parseFloat(reserveB) > 0 ? <span className="text-[#6B7280] text-xs ml-1">{formatUsd(reserveB, p)}</span> : null;
                        })()}
                      </>
                    : <span className="text-[#6B7280]">Loading...</span>}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* User Position */}
        {userPosition && userPosition.active && (
          <div className="mt-4 bg-[#0A0D14] border border-[#00CC99]/20 rounded-[10px] p-4">
            {/* Header */}
            <div className="flex items-center gap-1 mb-2">
              <div className="text-xs text-[#6B7280]">Your Position</div>
              <InformationCircleIcon
                className="h-[13px] w-[13px] text-[#6B7280] cursor-pointer flex-shrink-0"
                onClick={() => onDialogOpen(
                  "Your share of the crowdfunded vault. The LP tokens you hold represent your contribution.\n\nBigger contribution = bigger cut of every trade fee + bigger share of AQUA rewards. All reinvested for you, every 4 hours.\n\nWithdraw anytime. No lockups.",
                  "Your Position"
                )}
              />
            </div>

            {/* LP + USD inline */}
            <div className="text-base font-semibold text-white mb-2 flex flex-wrap items-baseline gap-x-1 leading-snug">
              <span className="whitespace-nowrap">{parseFloat(userPosition.user_lp_amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} LP</span>
              <span className="text-[#6B7280] text-xs font-normal whitespace-nowrap">
                {positionValueUsd === null ? "" : positionValueUsd > 0 ? `($${formatPositionUsd(positionValueUsd)})` : "(...)"}
              </span>
              <span className="text-[#6B7280] text-xs font-normal whitespace-nowrap">your slice of the pool</span>
            </div>

            {/* What this position has earned: LP gained from compounding, with its USD value */}
            {(() => {
              const gainLp = parseFloat(userCompoundGains?.compoundGainLp ?? "0");
              const lp = parseFloat(userPosition.user_lp_amount || "0");
              const gainUsd = gainLp > 0 && lp > 0 && positionValueUsd ? (gainLp / lp) * positionValueUsd : null;
              return (
                <div className="bg-[#070910] border border-[#1C2235] rounded-[8px] px-3 py-2 mb-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] text-[#B1B3B8]">Earned so far</span>
                    <InformationCircleIcon
                      className="h-[13px] w-[13px] text-[#6B7280] cursor-pointer flex-shrink-0"
                      onClick={() => onDialogOpen(
                        "LP tokens added to your position by auto-compounding since you deposited, and what they are worth now.\n\nThey come from swap fees and, for AQUA-only deposits, AQUA rewards.\n\nEvery 4 hours the vault reinvests, so this number only moves up.",
                        "Earned So Far"
                      )}
                    />
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-[#00CC99]">+{gainLp.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} LP</div>
                    {gainUsd !== null && <div className="text-[10px] text-[#6B7280]">{`$${formatPositionUsd(gainUsd)}`}</div>}
                    {gainLp <= 0 && compoundStats && compoundStats.lastCompoundTime > 0 && (
                      <div className="text-[10px] text-[#6B7280]">No compounds since {new Date(compoundStats.lastCompoundTime * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Auto-compound phase 2: the part of this position that auto-restake deposited.
                Auto-restake shares are ordinary shares, so the totals above already include them. */}
            {(() => {
              const st = ac.info?.stats;
              if (!st || st.vaultShares <= 0 || !ac.config || selectedPool?.pool_id !== ac.config.vaultPoolId) return null;
              const userSharesRaw = parseFloat(userPosition.share_ratio || "0");
              const lp = parseFloat(userPosition.user_lp_amount || "0");
              const autoLp = userSharesRaw > 0 ? (lp * st.vaultShares * 1e7) / userSharesRaw : 0;
              const autoUsd = lp > 0 && positionValueUsd ? (autoLp / lp) * positionValueUsd : null;
              return (
                <div className="bg-[#070910] border border-[#00CC99]/40 rounded-[8px] px-3 py-2 mb-2 flex items-center justify-between gap-2">
                  <div className="flex flex-col">
                    <span className="text-[11px] text-[#B1B3B8]">of which from auto-restake</span>
                    <span className="text-[10px] text-[#6B7280]">
                      {st.toVault.toLocaleString("en-US", { maximumFractionDigits: 2 })} AQUA deposited for you
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-[#00CC99]">{autoLp.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} LP</div>
                    {autoUsd !== null && <div className="text-[10px] text-[#6B7280]">{`$${formatPositionUsd(autoUsd)}`}</div>}
                  </div>
                </div>
              );
            })()}

            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
              <div className="flex items-center gap-1">
                <div className="text-xs text-[#00CC99] font-medium flex items-center gap-1 whitespace-nowrap">
                  <CheckCircleIcon className="h-[14px] w-[14px] flex-shrink-0" />
                  Earning Boosted Rewards
                </div>
                <InformationCircleIcon
                  className="h-[13px] w-[13px] text-[#6B7280] cursor-pointer flex-shrink-0"
                  onClick={() => onDialogOpen(
                    "Your position earns the highest reward tier the pool qualifies for, thanks to WhaleHub's pooled ICE voting power. Solo, you would earn the base rate.\n\nAQUA-only deposits also receive AQUA rewards on top of swap fees.",
                    "Earning Boosted Rewards"
                  )}
                />
              </div>
              {/* Compounding health, driven by the last compound timestamp rather than a
                  lifetime counter: the cadence has changed several times, so a count
                  says nothing, but a stale timestamp exposes a stalled cron. */}
              {compoundStats && compoundStats.lastCompoundTime > 0 && (() => {
                const ageSec = Math.max(0, Math.floor(Date.now() / 1000) - compoundStats.lastCompoundTime);
                const stalled = ageSec > 8 * 3600; // twice the 4h cadence
                const ago = ageSec < 3600 ? `${Math.max(1, Math.floor(ageSec / 60))}m ago`
                  : ageSec < 86400 ? `${Math.floor(ageSec / 3600)}h ago`
                  : `${Math.floor(ageSec / 86400)}d ago`;
                return (
                  <div className="flex items-center gap-1">
                    <div className={clsx("text-[10px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap", stalled ? "text-[#F5B942] border-[#F5B942]/40 bg-[#F5B942]/10" : "text-[#00CC99] border-[#00CC99]/40 bg-[#00CC99]/10")}>
                      {stalled ? "Compounding stalled" : "Compounding active"} · {ago}
                    </div>
                    <InformationCircleIcon
                      className="h-[13px] w-[13px] text-[#6B7280] cursor-pointer flex-shrink-0"
                      onClick={() => onDialogOpen(
                        "Every 4 hours the vault takes the fees and rewards it earned and puts them back into your position. This shows when that last happened.\n\nActive: the last compound was within the expected window.\nStalled: it has been more than 8 hours, so nothing new has been added to positions since then. Existing positions are unaffected; compounding resumes automatically once the schedule is running again.",
                        "Compounding Status"
                      )}
                    />
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex mt-6 space-x-1 bg-[#0A0D14] p-1 rounded-[10px] border border-[#1C2235]">
          <button
            className={clsx(
              "flex-1 py-2 rounded-[8px] text-sm font-medium transition-colors",
              activeTab === "deposit"
                ? "bg-[#00CC99] text-white shadow-sm"
                : "text-[#6B7280] hover:text-white"
            )}
            onClick={() => setActiveTab("deposit")}
          >
            Deposit
          </button>
          <button
            className={clsx(
              "flex-1 py-2 rounded-[8px] text-sm font-medium transition-colors",
              activeTab === "withdraw"
                ? "bg-[#00CC99] text-white shadow-sm"
                : "text-[#6B7280] hover:text-white"
            )}
            onClick={() => setActiveTab("withdraw")}
          >
            Withdraw
          </button>
        </div>

        {/* Deposit Tab */}
        {activeTab === "deposit" && selectedPool && (
          <div className="mt-5 space-y-4">
            <div className="text-xs text-[#B1B3B8] italic mb-1">
              Auto-compounded every 4 hours. Your share of pool fees and AQUA rewards is reinvested for you automatically. No claiming, no manual work.
            </div>

            {/* Deposit class: AQUA only (incentivized) vs AQUA + BLUB pair */}
            <div className="flex items-center gap-1 mb-1.5">
              <span className="text-sm text-[#B1B3B8]">How do you want to deposit?</span>
              <InformationCircleIcon
                className="h-[13px] w-[13px] text-[#6B7280] cursor-pointer flex-shrink-0"
                onClick={() => onDialogOpen(
                  "AQUA only (Boosted): deposit AQUA on its own and the vault pairs it into the AQUA-BLUB pool for you. You earn swap fees plus AQUA rewards, auto-compounded every 4 hours.\n\nAQUA + BLUB: deposit both tokens in pool ratio. You earn swap fees only, auto-compounded. AQUA rewards go to AQUA-only deposits.\n\nBLUB-only deposits are not offered: they push the pool off ratio.",
                  "Deposit Options"
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { setSingleAsset(true); setDepositAmount1(""); setDepositAmount2(""); }}
                className={clsx(
                  "text-left rounded-[10px] border px-3 py-2.5 transition-colors",
                  singleAsset ? "border-[#00CC99] bg-[#00CC99]/10" : "border-[#1C2235] bg-[#0A0D14] hover:border-[#2A3050]"
                )}
              >
                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm font-medium text-white">
                  {TOKEN_LOGOS["AQUA"] && <img src={TOKEN_LOGOS["AQUA"]} alt="AQUA" className="w-4 h-4 rounded-full flex-shrink-0" />}
                  <span className="whitespace-nowrap">AQUA only</span>
                  <span className="sm:ml-auto text-[10px] uppercase tracking-wider text-[#00CC99] font-bold">Boosted</span>
                </div>
                {vaultApy !== null && (
                  <div className="mt-1 flex items-center gap-1">
                    <span className="text-base font-bold text-[#00CC99]">{fmtApy(vaultApy)}% APY</span>
                    <QuestionMarkCircleIcon
                      aria-label="What is this APY?"
                      className="h-[14px] w-[14px] text-[#B1B3B8] hover:text-white cursor-pointer flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDialogOpen(VAULT_APY_INFO, "Vault APY");
                      }}
                    />
                  </div>
                )}
                <div className="text-[10px] text-[#B1B3B8] mt-1 leading-snug">
                  Swap fees + AQUA rewards
                </div>
              </button>
              <button
                onClick={() => { setSingleAsset(false); setDepositAmount1(""); setDepositAmount2(""); }}
                className={clsx(
                  "text-left rounded-[10px] border px-3 py-2.5 transition-colors",
                  !singleAsset ? "border-[#00CC99] bg-[#00CC99]/10" : "border-[#1C2235] bg-[#0A0D14] hover:border-[#2A3050]"
                )}
              >
                <div className="flex items-center gap-1.5 text-sm font-medium text-white whitespace-nowrap">
                  {TOKEN_LOGOS["AQUA"] && <img src={TOKEN_LOGOS["AQUA"]} alt="AQUA" className="w-4 h-4 rounded-full flex-shrink-0" />}
                  {TOKEN_LOGOS["BLUB"] && <img src={TOKEN_LOGOS["BLUB"]} alt="BLUB" className="w-4 h-4 rounded-full -ml-2.5 flex-shrink-0" />}
                  AQUA + BLUB
                </div>
                <div className="text-[10px] text-[#B1B3B8] mt-1 leading-snug">Swap fees only</div>
              </button>
            </div>
            {!singleAsset && (
              <div className="text-[11px] text-[#F5B942] bg-[#F5B942]/10 border border-[#F5B942]/30 rounded-[8px] px-3 py-2">
                Pair deposits earn swap fees only. AQUA rewards go to AQUA-only deposits. Choose AQUA only to earn them.
              </div>
            )}

            {singleAsset ? (
              /* Single-asset mode: token selector + single input */
              <div>
                {/* Token selector */}
                <div className="flex items-center gap-2 mb-2">
                  {singleAssetOptions.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => {
                        setSingleAssetToken(t.key);
                        setDepositAmount1("");
                      }}
                      className={clsx(
                        "flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-sm font-medium transition-colors border",
                        singleAssetToken === t.key
                          ? "border-[#00CC99] bg-[#00CC99]/10 text-white"
                          : "border-[#1C2235] bg-[#0A0D14] text-[#6B7280] hover:border-[#2A3050]"
                      )}
                    >
                      {TOKEN_LOGOS[t.code] && (
                        <img src={TOKEN_LOGOS[t.code]} alt={t.code} className="w-4 h-4 rounded-full flex-shrink-0" />
                      )}
                      {t.code}
                    </button>
                  ))}
                </div>

                <div className="flex justify-between items-center mb-1.5">
                  <label className="text-xs text-[#6B7280] uppercase tracking-wider">
                    {singleAssetToken === "a" ? selectedPool.token_a_code : selectedPool.token_b_code}
                  </label>
                  <span className="text-xs text-[#6B7280]">
                    Balance: {isLoadingBalances ? "..." : fmtNum(singleAssetToken === "a" ? balanceA : balanceB)}
                  </span>
                </div>
                <div className="flex items-center bg-[#0A0D14] border border-[#1C2235] rounded-[10px] px-3 py-2 focus-within:border-[#00CC99] transition-colors">
                  {TOKEN_LOGOS[singleAssetToken === "a" ? selectedPool.token_a_code : selectedPool.token_b_code] && (
                    <img
                      src={TOKEN_LOGOS[singleAssetToken === "a" ? selectedPool.token_a_code : selectedPool.token_b_code]}
                      alt={singleAssetToken === "a" ? selectedPool.token_a_code : selectedPool.token_b_code}
                      className="w-5 h-5 rounded-full mr-2 flex-shrink-0"
                    />
                  )}
                  <Input
                    placeholder="0"
                    value={depositAmount1}
                    type="text"
                    inputMode="decimal"
                    className="flex-1 bg-transparent text-white text-sm focus:outline-none focus:ring-0 border-none min-w-0"
                    onChange={(e) => handleAmount1Change(e.target.value)}
                  />
                  <button
                    className="ml-2 text-xs text-[#00CC99] hover:text-white transition-colors font-medium flex-shrink-0"
                    onClick={() => handleAmount1Change(singleAssetToken === "a" ? balanceA : balanceB)}
                  >
                    MAX
                  </button>
                </div>
              </div>
            ) : (
              /* Dual-asset mode: Token A + Token B inputs */
              <>
                {/* Token A */}
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label className="text-xs text-[#6B7280] uppercase tracking-wider">{selectedPool.token_a_code}</label>
                    <span className="text-xs text-[#6B7280]">
                      Balance: {isLoadingBalances ? "..." : fmtNum(balanceA)}
                    </span>
                  </div>
                  <div className="flex items-center bg-[#0A0D14] border border-[#1C2235] rounded-[10px] px-3 py-2 focus-within:border-[#00CC99] transition-colors">
                    {TOKEN_LOGOS[selectedPool.token_a_code] && (
                      <img src={TOKEN_LOGOS[selectedPool.token_a_code]} alt={selectedPool.token_a_code} className="w-5 h-5 rounded-full mr-2 flex-shrink-0" />
                    )}
                    <Input
                      placeholder="0"
                      value={depositAmount1}
                      type="text"
                      inputMode="decimal"
                      className="flex-1 bg-transparent text-white text-sm focus:outline-none focus:ring-0 border-none min-w-0"
                      onChange={(e) => handleAmount1Change(e.target.value)}
                    />
                    <button
                      className="ml-2 text-xs text-[#00CC99] hover:text-white transition-colors font-medium flex-shrink-0"
                      onClick={() => handleAmount1Change(balanceA)}
                    >
                      MAX
                    </button>
                  </div>
                </div>

                {/* Token B */}
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label className="text-xs text-[#6B7280] uppercase tracking-wider">{selectedPool.token_b_code}</label>
                    <span className="text-xs text-[#6B7280]">
                      Balance: {isLoadingBalances ? "..." : fmtNum(balanceB)}
                    </span>
                  </div>
                  <div className="flex items-center bg-[#0A0D14] border border-[#1C2235] rounded-[10px] px-3 py-2 focus-within:border-[#00CC99] transition-colors">
                    {TOKEN_LOGOS[selectedPool.token_b_code] && (
                      <img src={TOKEN_LOGOS[selectedPool.token_b_code]} alt={selectedPool.token_b_code} className="w-5 h-5 rounded-full mr-2 flex-shrink-0" />
                    )}
                    <Input
                      placeholder="0"
                      value={depositAmount2}
                      type="text"
                      inputMode="decimal"
                      className="flex-1 bg-transparent text-white text-sm focus:outline-none focus:ring-0 border-none min-w-0"
                      onChange={(e) => handleAmount2Change(e.target.value)}
                    />
                    <button
                      className="ml-2 text-xs text-[#00CC99] hover:text-white transition-colors font-medium flex-shrink-0"
                      onClick={() => handleAmount2Change(balanceB)}
                    >
                      MAX
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Transaction Summary */}
            {depositAmount1 && parseFloat(depositAmount1) > 0 && (singleAsset || (depositAmount2 && parseFloat(depositAmount2) > 0)) && (
              <div className="bg-[#070910] border border-[#1C2235] rounded-[10px] p-4">
                <div className="text-xs text-[#6B7280] uppercase tracking-wider mb-3">Transaction Summary</div>
                <div className="space-y-2.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#B1B3B8]">You deposit</span>
                    <div className="text-right">
                      {singleAsset ? (
                        <div className="text-white">
                          {fmtNum(depositAmount1)} {singleAssetToken === "a" ? selectedPool.token_a_code : selectedPool.token_b_code}
                        </div>
                      ) : (
                        <>
                          <div className="text-white">{fmtNum(depositAmount1)} {selectedPool.token_a_code}</div>
                          {depositAmount2 && parseFloat(depositAmount2) > 0 && (
                            <div className="text-white">{fmtNum(depositAmount2)} {selectedPool.token_b_code}</div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="border-t border-[#1C2235]" />
                  <div className="flex justify-between">
                    <span className="text-[#B1B3B8]">Expected LP tokens</span>
                    <span className="text-white font-medium">
                      {quotedLp === undefined
                        ? "…"
                        : quotedLp === null
                        ? "unavailable"
                        : `~${fmtNum(quotedLp)}`}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#B1B3B8]">Min LP ({slippageTolerance}% slippage)</span>
                    <span className="text-[#00CC99] font-medium">
                      {quotedLp === undefined || quotedLp === null
                        ? "—"
                        : `~${fmtNum(parseFloat(quotedLp) * (1 - slippageTolerance / 100))}`}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <Button
              className="rounded-[12px] py-5 px-4 text-white w-full bg-[linear-gradient(180deg,_#00CC99_0%,_#005F99_100%)] text-base font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleDeposit}
              disabled={isDepositing || !selectedPool}
            >
              {isDepositing ? (
                <div className="flex justify-center items-center gap-2">
                  <span>Depositing...</span>
                  <TailSpin height="18" width="18" color="#ffffff" ariaLabel="loading" radius="1" visible={true} />
                </div>
              ) : (
                <span>Deposit & Earn</span>
              )}
            </Button>
          </div>
        )}

        {/* Withdraw Tab */}
        {activeTab === "withdraw" && selectedPool && (
          <div className="mt-5 space-y-4">
            {!userPosition || !userPosition.active ? (
              <div className="text-center py-10 text-[#6B7280] text-sm">
                No position to withdraw
              </div>
            ) : (
              <>
                {/* Position card */}
                <div className="bg-[#0A0D14] border border-[#1C2235] rounded-[10px] p-4 space-y-2 text-sm">
                  <div className="text-xs text-[#6B7280] uppercase tracking-wider mb-2">Your Position</div>
                  <div className="flex justify-between text-white">
                    <span className="text-[#B1B3B8]">LP Tokens</span>
                    <span className="font-medium">
                      {parseFloat(userPosition.user_lp_amount || "0").toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      <span className="text-[#6B7280] text-xs font-normal ml-1">
                        {positionValueUsd === null ? "" : positionValueUsd > 0 ? `($${formatPositionUsd(positionValueUsd)})` : "(...)"}
                      </span>
                    </span>
                  </div>
                  <div className="flex justify-between text-white">
                    <span className="text-[#B1B3B8]">Pool Share</span>
                    <span>{fmtNum(userPosition.percentage || "0", 2)}%</span>
                  </div>
                  {userCompoundGains && parseFloat(userCompoundGains.compoundGainLp) > 0 && (
                    <div className="flex justify-between text-[#00CC99]">
                      <span>Compound Gains</span>
                      <span>+{fmtNum(userCompoundGains.compoundGainLp)} LP</span>
                    </div>
                  )}
                  {compoundStats && compoundStats.lastCompoundTime > 0 && (
                    <div className="pt-2 border-t border-[#1C2235] text-xs text-[#6B7280]">
                      Last compound: {new Date(compoundStats.lastCompoundTime * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </div>
                  )}
                </div>

                {/* Withdrawal % */}
                <div>
                  <label className="text-xs text-[#6B7280] uppercase tracking-wider mb-2 block">Withdrawal Amount</label>
                  <div className="flex items-center bg-[#0A0D14] border border-[#1C2235] rounded-[10px] px-3 py-2 focus-within:border-[#00CC99] transition-colors">
                    <Input
                      type="number"
                      min="1" max="100"
                      value={withdrawPercent}
                      className="flex-1 bg-transparent text-white text-sm focus:outline-none focus:ring-0 border-none"
                      onChange={(e) => setWithdrawPercent(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                    />
                    <span className="text-[#6B7280] text-sm ml-1">%</span>
                  </div>
                  <div className="flex items-center space-x-2 mt-2">
                    {[25, 50, 75, 100].map((preset) => (
                      <button
                        key={preset}
                        className={clsx(
                          "flex-1 py-1.5 rounded-[6px] text-xs font-medium transition-colors",
                          withdrawPercent === preset
                            ? "bg-[#00CC99] text-white"
                            : "bg-[#1C2235] text-[#B1B3B8] hover:bg-[#2A3050]"
                        )}
                        onClick={() => setWithdrawPercent(preset)}
                      >
                        {preset}%
                      </button>
                    ))}
                  </div>
                </div>

                {/* Withdraw-as selector. Taking a single leg out of a stable
                    pool pays an imbalance fee, so it is offered rather than
                    defaulted — the pro-rata exit stays the cheap one. */}
                <div className="mb-3">
                  <div className="text-xs text-[#6B7280] uppercase tracking-wider mb-2">Receive as</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => setWithdrawCoinIndex(null)}
                      className={clsx(
                        "px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors",
                        withdrawCoinIndex === null
                          ? "bg-[#0B6AA6]/20 border-[#0B6AA6] text-white"
                          : "bg-transparent border-[#1C2235] text-[#B1B3B8] hover:border-[#2A3550]"
                      )}
                    >
                      Both tokens
                    </button>
                    {POOL_COIN_ORDER.map((code, idx) => (
                      <button
                        key={code}
                        onClick={() => setWithdrawCoinIndex(idx)}
                        className={clsx(
                          "px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors",
                          withdrawCoinIndex === idx
                            ? "bg-[#0B6AA6]/20 border-[#0B6AA6] text-white"
                            : "bg-transparent border-[#1C2235] text-[#B1B3B8] hover:border-[#2A3550]"
                        )}
                      >
                        {code} only
                      </button>
                    ))}
                  </div>
                  {withdrawCoinIndex !== null && (
                    <div className="text-[11px] text-[#6B7280] mt-2">
                      Taking one token out of a balanced pool costs an imbalance fee that grows with size. Your slippage tolerance ({slippageTolerance}%) is the floor — the withdrawal reverts rather than filling below it.
                    </div>
                  )}
                </div>

                {/* Receipt summary */}
                <div className="bg-[#070910] border border-[#1C2235] rounded-[10px] p-4">
                  <div className="text-xs text-[#6B7280] uppercase tracking-wider mb-3">You will receive</div>
                  <div className="space-y-2.5 text-sm">
                    {withdrawCoinIndex === null ? (
                      <>
                        <div className="flex justify-between text-white">
                          <span className="text-[#B1B3B8]">{selectedPool.token_a_code}</span>
                          <span>~{fmtNum(getEstimatedWithdrawAmounts().estimatedA)}</span>
                        </div>
                        <div className="flex justify-between text-white">
                          <span className="text-[#B1B3B8]">{selectedPool.token_b_code}</span>
                          <span>~{fmtNum(getEstimatedWithdrawAmounts().estimatedB)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between text-white">
                        <span className="text-[#B1B3B8]">{POOL_COIN_ORDER[withdrawCoinIndex]}</span>
                        {/* Pro-rata value of the position, shown before the
                            imbalance fee the pool charges for a single leg —
                            so treat it as an upper bound, not a quote. */}
                        <span>
                          ≤ {fmtNum(
                            withdrawCoinIndex === 0
                              ? getEstimatedWithdrawAmounts().estimatedB
                              : getEstimatedWithdrawAmounts().estimatedA
                          )}
                        </span>
                      </div>
                    )}
                    <div className="border-t border-[#1C2235] pt-2.5">
                      <div className="text-xs text-[#6B7280] mb-2">Minimum ({slippageTolerance}% slippage)</div>
                      <div className="flex justify-between text-[#00CC99]">
                        <span>{selectedPool.token_a_code}</span>
                        <span>{fmtNum(parseFloat(getEstimatedWithdrawAmounts().estimatedA) * (1 - slippageTolerance / 100))}</span>
                      </div>
                      <div className="flex justify-between text-[#00CC99] mt-1">
                        <span>{selectedPool.token_b_code}</span>
                        <span>{fmtNum(parseFloat(getEstimatedWithdrawAmounts().estimatedB) * (1 - slippageTolerance / 100))}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  className="rounded-[12px] py-5 px-4 text-white w-full bg-[linear-gradient(180deg,_#00CC99_0%,_#005F99_100%)] text-base font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleWithdraw}
                  disabled={isWithdrawing}
                >
                  {isWithdrawing ? (
                    <div className="flex justify-center items-center gap-2">
                      <span>Withdrawing...</span>
                      <TailSpin height="18" width="18" color="#ffffff" ariaLabel="loading" radius="1" visible={true} />
                    </div>
                  ) : (
                    <span>Withdraw {withdrawPercent}%</span>
                  )}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      <DialogC
        msg={dialogMsg}
        openDialog={openDialog}
        dialogTitle={dialogTitle}
        closeModal={closeModal}
      />
    </div>
  );
}

export default AddLiquidity;
